// Feature service for managing restaurant features
import { supabase } from '../utils/supabaseClient';
import { Feature } from '../types/custom';
import { Json } from '../types/supabase';

export interface FeatureFilters {
    branch_id?: string;
    name?: string;
    search?: string;
}

export interface FeatureCreateData {
    name: string;
    description?: string | null;
    icon?: string | null;
    branch_id?: string | null;
    children?: Json | null;
    ar?: Json | null;
}

export interface FeatureUpdateData {
    name?: string;
    description?: string | null;
    icon?: string | null;
    children?: Json | null;
    ar?: Json | null;
}

export const featureService = {
    // Get all features for a branch
    async getFeaturesByBranch(branchId: string): Promise<Feature[]> {
        try {
            const { data, error } = await supabase
                .from('features')
                .select('*')
                .eq('branch_id', branchId)
                .order('created_at', { ascending: true });

            if (error) {
                console.error('Error fetching features:', error);
                throw error;
            }

            return data || [];
        } catch (error) {
            console.error('Error in getFeaturesByBranch:', error);
            throw error;
        }
    },

    // Get all available features (global/template features)
    async getAvailableFeatures(): Promise<Feature[]> {
        try {
            const { data, error } = await supabase
                .from('features')
                .select('*')
                .is('branch_id', null)
                .order('name', { ascending: true });

            if (error) {
                console.error('Error fetching available features:', error);
                throw error;
            }

            return data || [];
        } catch (error) {
            console.error('Error in getAvailableFeatures:', error);
            throw error;
        }
    },

    // Create a new feature
    async createFeature(featureData: FeatureCreateData): Promise<Feature> {
        try {
            const { data, error } = await supabase
                .from('features')
                .insert([featureData])
                .select()
                .single();

            if (error) {
                console.error('Error creating feature:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in createFeature:', error);
            throw error;
        }
    },

    // Update an existing feature
    async updateFeature(featureId: string, updateData: FeatureUpdateData): Promise<Feature> {
        try {
            const { data, error } = await supabase
                .from('features')
                .update(updateData)
                .eq('id', featureId)
                .select()
                .single();

            if (error) {
                console.error('Error updating feature:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in updateFeature:', error);
            throw error;
        }
    },

    // Delete a feature
    async deleteFeature(featureId: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('features')
                .delete()
                .eq('id', featureId);

            if (error) {
                console.error('Error deleting feature:', error);
                throw error;
            }
        } catch (error) {
            console.error('Error in deleteFeature:', error);
            throw error;
        }
    },

    // Clone a global feature to a specific branch
    async cloneFeatureToBranch(globalFeature: Feature, branchId: string): Promise<Feature> {
        try {
            const featureData: FeatureCreateData = {
                name: globalFeature.name,
                description: globalFeature.description,
                icon: globalFeature.icon,
                branch_id: branchId,
                children: globalFeature.children
            };

            return await this.createFeature(featureData);
        } catch (error) {
            console.error('Error in cloneFeatureToBranch:', error);
            throw error;
        }
    },

    // Update feature children (settings)
    async updateFeatureChildren(featureId: string, children: Json): Promise<Feature> {
        try {
            return await this.updateFeature(featureId, { children });
        } catch (error) {
            console.error('Error in updateFeatureChildren:', error);
            throw error;
        }
    }
};